import { useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import Input from '../Input/Input';
import MultiSelect from '../Input/MultiSelect';
import { DataContext } from '../../App';
import {
  Dashboard,
  WebsiteSettings,
  WebsiteFormErrors,
  ActiveWebsiteSettings
} from '../../types/types';
import { AdminDropdown } from '../Input/Dropdown';
import {
  AppRoute,
  ButtonType,
  InputVariant,
  PillVariant,
  WebsiteSettingKey,
  WebsiteSettingsOption
} from '../../types/enums';
import Button from '../Button/Button';
import { isValidUrl } from '../../utils/common';
import Pill from '../Pill/Pill';
import Tooltip from '../Tooltip/Tooltip';
import Toggle from '../Toggle/Toggle';
import ImageInput from '../Input/ImageInput';

import { ReactComponent as Arrow } from './chevron-down-solid.svg';

import styles from './WebsiteForm.module.scss';

export const DEFAULT_WEBSITE_FORM_STATE: WebsiteSettings = {
  url: '',
  name: '',
  dashboardIds: [],
  httpStatusRule: WebsiteSettingsOption.IS_NOT_ONE_OF,
  httpStatusCode: '200, 301, 302, 304',
  keywordRule: WebsiteSettingsOption.INCLUDES_KEYWORD,
  keywords: '',
  loadTimeError: WebsiteSettingsOption.SECOND_10,
  sslExpireError: WebsiteSettingsOption.EXPIRED,
  loadTimeWarning: WebsiteSettingsOption.SECOND_10,
  sslExpireWarning: WebsiteSettingsOption.DAYS_10,
  active: {
    [WebsiteSettingKey.HTTP_STATUS_RULE]: true,
    [WebsiteSettingKey.KEYWORD_RULE]: false,
    [WebsiteSettingKey.LOAD_TIME_ERROR]: true,
    [WebsiteSettingKey.SSL_EXPIRE_ERROR]: true,
    [WebsiteSettingKey.LOAD_TIME_WARNING]: false,
    [WebsiteSettingKey.SSL_EXPIRE_WARNING]: false
  },
  iconPath: '',
  errors: {}
};

interface WebsiteFormProps {
  onSave: (form: WebsiteSettings) => void;
  initialState?: WebsiteSettings;
}

const WebsiteForm = ({ onSave, initialState }: WebsiteFormProps) => {
  const navigate = useNavigate();
  const { dashboards, refreshDashboards } = useContext(DataContext);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(!!initialState);
  const [formState, setFormState] = useState(
    initialState || {
      ...DEFAULT_WEBSITE_FORM_STATE,
      dashboardIds: dashboards.length === 1 ? [dashboards[0].id] : []
    }
  );

  useEffect(() => {
    if (!dashboards || dashboards.length === 0) {
      refreshDashboards();
    }
  }, []);

  const formatUrl = useCallback(() => {
    let urlToCheck = formState.url;
    if (!urlToCheck.startsWith('http://') && !urlToCheck.startsWith('https://')) {
      urlToCheck = 'https://' + urlToCheck;
    }

    if (urlToCheck !== formState.url) {
      setFormState((state) => ({ ...state, url: urlToCheck }));
    }
  }, [formState.url]);

  useEffect(() => {
    if (!isNameTouched && formState.url) {
      try {
        const { hostname, port, pathname, hash, search } = new URL(formState.url);
        const name = [
          hostname.replace('www.', ''),
          port,
          pathname.split('.')[0].replaceAll('/', ' ').trim(),
          hash.replace('#', ''),
          search.replace('?', '').replaceAll('&', ' ').trim()
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        setFormState((state) => ({ ...state, name }));
      } catch {
        console.warn('Automatic website name generation failed.');
      }
    }
  }, [formState.url, isNameTouched]);

  const checkUrl = useCallback(async () => {
    let urlToCheck = formState.url;

    if (!urlToCheck.startsWith('http://') && !urlToCheck.startsWith('https://')) {
      urlToCheck = 'https://' + urlToCheck;
    }

    if (formState.url) {
      setFormState((form) => ({
        ...form,
        errors: { ...form.errors, url: isValidUrl(urlToCheck) ? '' : 'Please add a valid URL.' }
      }));
    }
  }, [formState.url]);

  useEffect(() => {
    const timeout = setTimeout(checkUrl, 300);
    return () => clearTimeout(timeout);
  }, [checkUrl]);

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const errors = validateForm(formState);
      setFormState((state) => ({ ...state, errors }));

      if (Object.keys(errors).length === 0) {
        onSave(formState);
      }
    },
    [formState]
  );

  const toggleRule = useCallback((rule: keyof ActiveWebsiteSettings) => {
    setFormState((state) => ({
      ...state,
      active: {
        ...state.active,
        [rule]: !state.active[rule]
      }
    }));
  }, []);

  return (
    <div>
      <form onSubmit={onSubmit}>
        <Input
          setValue={(val) => setFormState((state) => ({ ...state, url: val }))}
          value={formState.url}
          placeholder="e.g. https://yourdomain.com"
          id="website-url"
          label="Page URL:"
          error={formState.errors.url}
          onBlur={formatUrl}
        />
        <Input
          setValue={(val) => {
            setFormState((state) => ({ ...state, name: val })), setIsNameTouched(true);
          }}
          value={formState.name}
          placeholder="e.g. Your Website"
          id="website-name"
          label="Website Name:"
          helperText="We will display this name on your status page."
          error={formState.errors.name}
        />
        <MultiSelect
          options={getDashboardOptions(dashboards)}
          checkedValues={formState.dashboardIds}
          setValue={(name, checked) => {
            if (checked) {
              setFormState((state) => ({ ...state, dashboardIds: [...state.dashboardIds, name] }));
            } else {
              setFormState((state) => ({
                ...state,
                dashboardIds: [...state.dashboardIds].filter((it) => it !== name)
              }));
            }
          }}
          title="Display the status on:"
          helperText="We'll display the status of this website on the following Dashboards."
        />
        <ImageInput
          value={formState.iconPath}
          setValue={(value) => setFormState((state) => ({ ...state, iconPath: value }))}
          id="icon"
          helperText="Upload an icon to be displayed in your dashboard."
          label="Website icon"
        />
        <div>Or use one of our icons</div>
        <div className={styles.icons}>
          {DEFAULT_ICON_PATHS.map((it) => (
            <img
              onClick={() => setFormState((state) => ({ ...state, iconPath: it }))}
              className={formState.iconPath === it ? styles.iconSelected : ''}
              key={it}
              src={process.env.REACT_APP_ASSETS_CDN + it}
              alt=""
            />
          ))}
        </div>
        <div>
          <h2 onClick={() => setIsAdvancedOptionsOpen((open) => !open)} className={styles.subtitle}>
            <Arrow
              className={classNames(styles.arrow, { [styles.arrowDown]: isAdvancedOptionsOpen })}
            />
            Advanced options
          </h2>
          {isAdvancedOptionsOpen && (
            <>
              <p>
                MonitorMonk will <Tooltip opener="regularly monitor your website">by default, MonitorMonk checks the website once every 1 minute</Tooltip> and report its status on your Dashboards.
                It does so by runnig a series of checks on your website.
                Finetune the rules MonitorMonk uses to run checks and report problems.
              </p>
              <div className={styles.settings}>
                <div className={styles.settingsRow}>
                  <Tooltip>
                      Use this rule to check what the web server thinks about its own responses.
                      <br/><br/>
                      For example, you can set MonitorMonk to alert you if the status code is not 200 OK.
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.ERROR}>error</Pill> IF
                  <Tooltip opener="HTTP Status Code">
                    HTTP Status Code is a way for web servers and APIs to tell what they think about their own responses.
                    <br/><br/>
                    Well-known status codes include 200 OK, 404 Not Found and 500 Internal Server Error.
                  </Tooltip>
                  <AdminDropdown
                    options={STATUS_CODE_OPTIONS}
                    value={formState.httpStatusRule}
                    setValue={(val) => setFormState((state) => ({ ...state, httpStatusRule: val }))}
                    id="httpStatus"
                    readonly={!formState.active[WebsiteSettingKey.HTTP_STATUS_RULE]}
                  />
                  <div
                    className={
                      formState.active[WebsiteSettingKey.HTTP_STATUS_RULE] ? '' : styles.readonlyRow
                    }>
                    <Input
                      id="httpStatusCode"
                      value={formState.httpStatusCode}
                      placeholder="e.g. 200, 301, 302"
                      setValue={(val) =>
                        setFormState((state) => ({ ...state, httpStatusCode: val }))
                      }
                      variant={InputVariant.INLINE}
                      error={formState.errors.httpStatusCode}
                      readonly={!formState.active[WebsiteSettingKey.HTTP_STATUS_RULE]}
                    />
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.HTTP_STATUS_RULE)}
                      active={formState.active[WebsiteSettingKey.HTTP_STATUS_RULE]}
                    />
                  </div>
                </div>

                <div className={styles.settingsRow}>
                  <Tooltip>
                    Use this rule to check the website content for specific keywords.
                    <br/><br/>
                    For example, you can set MonitorMonk to alert you if the response contains the word "Hacked".
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.ERROR}>error</Pill> IF
                  <Tooltip opener="HTTP Response Content">
                    HTTP Response Content is the data a web server or API sends back after processing a request.
                    <br/><br/>
                    In case of a website, this is typically the text on that website.
                  </Tooltip>
                  <AdminDropdown
                    options={KEYWORD_OPTIONS}
                    value={formState.keywordRule}
                    setValue={(val) => setFormState((state) => ({ ...state, keywordRule: val }))}
                    id="keywordRule"
                    readonly={!formState.active[WebsiteSettingKey.KEYWORD_RULE]}
                  />
                  <div
                    className={
                      formState.active[WebsiteSettingKey.KEYWORD_RULE] ? '' : styles.readonlyRow
                    }>
                    <Input
                      id="keywords"
                      value={formState.keywords}
                      placeholder="e.g. Hacked"
                      setValue={(val) => setFormState((state) => ({ ...state, keywords: val }))}
                      variant={InputVariant.INLINE}
                      readonly={!formState.active[WebsiteSettingKey.KEYWORD_RULE]}
                    />
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.KEYWORD_RULE)}
                      active={formState.active[WebsiteSettingKey.KEYWORD_RULE]}
                    />
                  </div>
                </div>

                <div className={styles.settingsRow}>
                  <Tooltip>
                    Use this rule to check if the website is fast enough.
                    <br/><br/>
                    For example, you can get an alert if the website takes more than 5 seconds to load.
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.ERROR}>error</Pill> IF
                  <Tooltip opener="HTTP Response Time">
                    HTTP Response Time is the time it takes for a web server or API to respond to a request.
                    <br/><br/>
                    In the case of a website, this is the time from when a user clicks on a link until the page starts to load in the browser.
                  </Tooltip>
                  takes more than
                  <AdminDropdown
                    options={RESPONSE_TIME_OPTIONS}
                    value={formState.loadTimeError}
                    setValue={(val) => setFormState((state) => ({ ...state, loadTimeError: val }))}
                    id="loadTimeError"
                    readonly={!formState.active[WebsiteSettingKey.LOAD_TIME_ERROR]}
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.LOAD_TIME_ERROR)}
                      active={formState.active[WebsiteSettingKey.LOAD_TIME_ERROR]}
                    />
                  </div>
                </div>

                <div className={styles.settingsRow}>
                  <Tooltip>
                    Use this rule to check if the SSL certificate is still valid.
                    <br/><br/>
                    For example, you can get an alert if the SSL certificate expires soon.
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.ERROR}>error</Pill> IF
                  <Tooltip opener="SSL Certificate">
                    The SSL Certificate secures the connection between a user's browser and a website.
                    <br/><br/>
                    If the SSL certificate is expired, users typically see a security warning in their browsers.
                    Also the communication between the user and the website is more vulnerable to attacks.
                  </Tooltip>
                  expires in
                  <AdminDropdown
                    options={SSL_OPTIONS}
                    value={formState.sslExpireError}
                    setValue={(val) => setFormState((state) => ({ ...state, sslExpireError: val }))}
                    id="sslExpireError"
                    readonly={!formState.active[WebsiteSettingKey.SSL_EXPIRE_ERROR]}
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.SSL_EXPIRE_ERROR)}
                      active={formState.active[WebsiteSettingKey.SSL_EXPIRE_ERROR]}
                    />
                  </div>
                </div>

                <div className={styles.settingsRow}>
                  <Tooltip>
                    Another rule to check if the website is fast enough.
                    <br/><br/>
                    This one is less strict, and gives you a warning instead of an error.
                    <br/><br/>
                    For example, you can get a warning if the website takes more than 10 seconds to load.
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.WARNING}>warning</Pill> IF
                  <Tooltip opener="HTTP Response Time">
                    HTTP Response Time is the time it takes for a web server or API to respond to a request.
                    <br/><br/>
                    In the case of a website, this is the time from when a user clicks on a link until the page starts to load in the browser.
                  </Tooltip>
                  takes more than
                  <AdminDropdown
                    options={RESPONSE_TIME_OPTIONS}
                    value={formState.loadTimeWarning}
                    setValue={(val) =>
                      setFormState((state) => ({ ...state, loadTimeWarning: val }))
                    }
                    id="loadTimeWarning"
                    readonly={!formState.active[WebsiteSettingKey.LOAD_TIME_WARNING]}
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.LOAD_TIME_WARNING)}
                      active={formState.active[WebsiteSettingKey.LOAD_TIME_WARNING]}
                    />
                  </div>
                </div>

                <div className={styles.settingsRow}>
                  <Tooltip>
                    Another rule to check if the SSL certificate is valid.
                    <br/><br/>
                    This one is less strict, and gives you a warning instead of an error.
                    <br/><br/>
                    For example, you can get a warning if the SSL certificate expires in 10 days.
                  </Tooltip>
                  REPORT <Pill variant={PillVariant.WARNING}>warning</Pill> IF
                  <Tooltip opener="SSL Certificate">
                    The SSL Certificate secures the connection between a user's browser and a website.
                    <br/><br/>
                    If the SSL certificate is expired, users typically see a security warning in their browsers.
                    Also the communication between the user and the website is more vulnerable to attacks.
                  </Tooltip>
                  <AdminDropdown
                    options={SSL_OPTIONS}
                    value={formState.sslExpireWarning}
                    setValue={(val) =>
                      setFormState((state) => ({ ...state, sslExpireWarning: val }))
                    }
                    id="sslExpireWarning"
                    readonly={!formState.active[WebsiteSettingKey.SSL_EXPIRE_WARNING]}
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    <Toggle
                      action={() => toggleRule(WebsiteSettingKey.SSL_EXPIRE_WARNING)}
                      active={formState.active[WebsiteSettingKey.SSL_EXPIRE_WARNING]}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex">
            <Button type={ButtonType.PRIMARY} isSubmit>
              Save resource
            </Button>
            <Button action={() => navigate(AppRoute.WEBSITES)} type={ButtonType.GHOST}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const STATUS_CODE_OPTIONS = [
  { name: 'is one of', value: WebsiteSettingsOption.IS_ONE_OF },
  { name: 'is not one of', value: WebsiteSettingsOption.IS_NOT_ONE_OF }
];

const KEYWORD_OPTIONS = [
  {
    name: "doesn't include keyword",
    value: WebsiteSettingsOption.NOT_INCLUDES_KEYWORD
  },
  { name: 'includes keyword', value: WebsiteSettingsOption.INCLUDES_KEYWORD }
];

const SSL_OPTIONS = [
  { name: 'expired', value: WebsiteSettingsOption.EXPIRED },
  { name: '1 day', value: WebsiteSettingsOption.DAYS_1 },
  { name: '2 days', value: WebsiteSettingsOption.DAYS_2 },
  { name: '3 days', value: WebsiteSettingsOption.DAYS_3 },
  { name: '5 days', value: WebsiteSettingsOption.DAYS_5 },
  { name: '10 days', value: WebsiteSettingsOption.DAYS_10 }
];

const RESPONSE_TIME_OPTIONS = [
  { name: '1 second', value: WebsiteSettingsOption.SECOND_1 },
  { name: '2 seconds', value: WebsiteSettingsOption.SECOND_2 },
  { name: '3 seconds', value: WebsiteSettingsOption.SECOND_3 },
  { name: '5 seconds', value: WebsiteSettingsOption.SECOND_5 },
  { name: '10 seconds', value: WebsiteSettingsOption.SECOND_10 }
];

const validateForm = (form: WebsiteSettings): WebsiteFormErrors =>
  Object.fromEntries(
    Object.entries(formValidator)
      .filter(([key, { validate }]) => !validate(form[key as WebsiteSettingKey]))
      .map(([key, { error }]) => [key, error])
  );

const httpStatusCodeRegex = /^[1-5][0-9]{2}$/gm;

const formValidator: Partial<
  Record<WebsiteSettingKey, { validate: (val: any) => boolean; error: string }>
> = {
  [WebsiteSettingKey.HTTP_STATUS_CODE]: {
    validate: (value: WebsiteSettings[WebsiteSettingKey.HTTP_STATUS_CODE]) =>
      value === '' || value.split(',').every((code) => code.trim().match(httpStatusCodeRegex)),
    error: 'Must contain only valid HTTP response status codes separated by comma.'
  },
  [WebsiteSettingKey.URL]: {
    validate: isValidUrl,
    error: 'Error: URL is not valid'
  },
  [WebsiteSettingKey.NAME]: {
    validate: (value: string) => value.length > 0,
    error: 'Error: Name is required'
  }
};

const getDashboardOptions = (dashboards: Dashboard[]) =>
  dashboards.map(({ id, name }) => ({ value: id, name }));

const DEFAULT_ICON_PATHS = [
  '/website-icons/default-icons/icon1.svg',
  '/website-icons/default-icons/icon2.svg',
  '/website-icons/default-icons/icon3.svg',
  '/website-icons/default-icons/icon4.svg',
  '/website-icons/default-icons/icon5.svg',
  '/website-icons/default-icons/icon6.svg',
  '/website-icons/default-icons/icon7.svg',
  '/website-icons/default-icons/icon8.svg',
  '/website-icons/default-icons/icon9.svg',
  '/website-icons/default-icons/icon10.svg'
];

export default WebsiteForm;
